/**
 * Background loading with priorities (owner requirement 2026-09-26,
 * requirement 4: "background loading with priorities via
 * requestIdleCallback"). The organism screen's own panels always load
 * first, eagerly, on their own critical path (`OrganismScreen.mountPanels`)
 * — nothing here delays or reorders that. Once they have, this schedules
 * one low-priority background task, during browser idle time, to warm
 * `RoleSnapshotRegistry` for every *other* known organism, so switching to
 * them later is instant (their `index.json`/`latest.json` round trip has
 * already happened) instead of paying for it on the click that navigates
 * there.
 */

import { getRoleSnapshot } from "./RoleSnapshotRegistry";
import type { RoleLocation } from "./snapshotIndex";

type IdleCallbackHandle = number;
type RequestIdleCallbackFn = (callback: () => void) => IdleCallbackHandle;

/** Schedules `fn` for a browser idle period, falling back to a `setTimeout`
 * on engines without `requestIdleCallback` (notably Safari, as of writing)
 * — either way `fn` is never run synchronously inline, so it can never
 * delay the caller's own critical-path work. */
export function runWhenIdle(fn: () => void): void {
  const scheduler = (globalThis as { requestIdleCallback?: RequestIdleCallbackFn })
    .requestIdleCallback;
  if (typeof scheduler === "function") {
    scheduler(fn);
  } else {
    setTimeout(fn, 1);
  }
}

/** Every known role location except `current` — the ones worth warming in
 * the background. Pure and easy to test; the actual fetching is a thin
 * wrapper around it (`prefetchOtherOrganisms`). */
export function otherRoleLocations(
  all: readonly RoleLocation[],
  current: RoleLocation,
): readonly RoleLocation[] {
  return all.filter((location) => location.organismId !== current.organismId);
}

/** Warms `RoleSnapshotRegistry` for every other known organism during idle
 * time. Best-effort only: a failed prefetch is silently dropped (the
 * registry itself already un-poisons on failure, see
 * `RoleSnapshotRegistry.getRoleSnapshot`'s doc comment) — a real visit to
 * that screen will simply retry the fetch on its own critical path exactly
 * as if this had never run. */
export function prefetchOtherOrganisms(all: readonly RoleLocation[], current: RoleLocation): void {
  runWhenIdle(() => {
    for (const location of otherRoleLocations(all, current)) {
      getRoleSnapshot(location).catch(() => {
        // Best-effort background warm-up — nothing to surface to the user.
      });
    }
  });
}
